import type { Properties } from 'js-java-properties'
import { listProperties, parse, removeProperty, stringify } from 'js-java-properties'

import {
  LEGACY_MODBUS_SERIAL_CONFIG_DEFAULTS,
  LEGACY_SERIAL_PORT_CONFIG_DEFAULTS,
} from '@/features/controller/configuration/models/raspiProperties/defaults'
import type { ConfigWithErrors } from '@/features/controller/configuration/models/raspiProperties/errors'
import { extractErrors } from '@/features/controller/configuration/models/raspiProperties/errors'
import {
  readLegacyModbusConnections,
  writeLegacyModbusConnections,
} from '@/features/controller/configuration/models/raspiProperties/modbus'
import {
  readLegacyModbusSerialConfig,
  writeLegacyModbusSerialConfig,
} from '@/features/controller/configuration/models/raspiProperties/modbusSerial'
import {
  describeDanglingExternalReferences,
  readExternalModbusClientReferences,
  readUnrecognisedModbusClientNames,
  writeNamedModbusClients,
} from '@/features/controller/configuration/models/raspiProperties/namedModbusClients'
import {
  readLegacySerialPortConfig,
  writeLegacySerialPortConfig,
} from '@/features/controller/configuration/models/raspiProperties/serialPort'
import {
  isTillquistEnabled,
  readTillquistConfig,
  writeTillquistConfig,
} from '@/features/controller/configuration/models/raspiProperties/tillquist'
import type {
  NamedModbusConnection,
  RaspiPropertiesConfig,
} from '@/features/controller/configuration/models/raspiProperties/types'

const PROPS_SERIAL_PORT_PREFIX = 'serialPort.'
const PROPS_MODBUS_SERIAL_PREFIX = 'modbus.serial.'

export function readRaspiPropertiesToModel(value: string): ConfigWithErrors<RaspiPropertiesConfig> {
  const properties = parse(value)

  const errors: string[] = []

  const legacySerialPortConfig = extractErrors(readLegacySerialPortConfig(properties), errors)
  const legacyModbusSerialConfig = extractErrors(readLegacyModbusSerialConfig(properties), errors)

  const {
    connections,
    errors: modbusErrors,
    highestMeterNumber,
  } = readLegacyModbusConnections(properties, legacySerialPortConfig.serialPort)
  errors.push(...modbusErrors)

  const externalModbusClientReferences = readExternalModbusClientReferences(properties)
  const unrecognisedModbusClientNames = readUnrecognisedModbusClientNames(properties)
  errors.push(
    ...describeDanglingExternalReferences(
      externalModbusClientReferences,
      connections
        .filter((conn): conn is NamedModbusConnection => conn['namedClient'] !== undefined)
        .map((conn) => conn.namedClient.name),
      unrecognisedModbusClientNames,
    ),
  )

  const config = {
    externalModbusClientReferences,
    legacyModbusConnections: connections,
    legacyModbusMeters: { highestMeterNumber },
    legacyModbusSerialConfig,
    legacySerialPortConfig,
    tillquistConfig: readTillquistConfig(properties),
    unrecognisedModbusClientNames,
  }

  return { config, errors }
}

/**
 * Remove properties that have been deprecated and removed and have no effect
 */
function removeLegacyProperties(properties: Properties) {
  const propsToRemove = [
    // RES-2172 Decided not to allow configuring of timeouts
    'tillquistMeter.timeoutMs',
    // This has been used as a reminder of the default in the file, it seems
    'defaulttillquistMeter.timeoutMs',
    // This was removed and is not used by raspi anymore
    'modbusMeter.numOfDevices',
  ]
  for (const key of propsToRemove) {
    removeProperty(properties, key)
  }
}

function namedModbusClients(model: RaspiPropertiesConfig) {
  return model.legacyModbusConnections
    .filter((conn): conn is NamedModbusConnection => conn['namedClient'] !== undefined)
    .map((conn) => conn.namedClient)
}

/**
 * Write raspi.properties model to text
 * @param previousText the previous version, needed to preserve comments, user-created properties order etc.
 * @param updatedModel the model to represent in the updated version
 * @param isSerialPortUsed set to true if the serial port is known to be used outside any modelled properties here.
 *  This will cause the serial port defaults to be saved.
 */
export function writeRaspiPropertiesToPropertiesText(
  previousText: string,
  updatedModel: RaspiPropertiesConfig,
  isSerialPortUsed: boolean,
): string {
  const properties = parse(previousText)

  removeLegacyProperties(properties)

  const hasAnyPreviousSerialPortProperties = listProperties(properties).some(
    ({ key }) => key.startsWith(PROPS_SERIAL_PORT_PREFIX) || key.startsWith(PROPS_MODBUS_SERIAL_PREFIX),
  )
  if (
    isSerialPortUsed ||
    hasAnyPreviousSerialPortProperties ||
    isTillquistEnabled(updatedModel.tillquistConfig) ||
    JSON.stringify(updatedModel.legacySerialPortConfig) !== JSON.stringify(LEGACY_SERIAL_PORT_CONFIG_DEFAULTS) ||
    JSON.stringify(updatedModel.legacyModbusSerialConfig) !== JSON.stringify(LEGACY_MODBUS_SERIAL_CONFIG_DEFAULTS)
  ) {
    // This mutation is conditional because some controllers might not use the serial port at all.
    // We have updated the defaults on read, and there's no point in making them explicit if not used.
    writeLegacySerialPortConfig(properties, updatedModel.legacySerialPortConfig)
    writeLegacyModbusSerialConfig(properties, updatedModel.legacyModbusSerialConfig)
  }
  writeTillquistConfig(properties, updatedModel.tillquistConfig)
  writeNamedModbusClients(properties, namedModbusClients(updatedModel))
  writeLegacyModbusConnections(
    properties,
    updatedModel.legacyModbusConnections,
    updatedModel.legacySerialPortConfig.serialPort,
  )
  return stringify(properties)
}
